import cn from 'classnames'
import CloseButton from 'ext-ui/components/CloseButton/CloseButton'
import { flyout_transitionSpeed } from 'ext-ui/style/transitionSpeeds'
import MagicalAIIcon from 'ext-ui/svg/icon/MagicalAIIcon'
import React, { ReactElement, useState } from 'react'
import Modal, { Props as ModalProps } from 'react-modal'
import styled from 'styled-components'

import style from './BaseFlyout.style'

export type BaseFlyoutProps = Omit<ModalProps, 'onRequestClose'> & {
  className?: string
  /** If `true`, this Modal is open */
  isOpen: boolean
  /**
   * Use the `focus-background` variant when the content behind the flyout
   * shouldn't be fully obscured (e.g. for a filters flyout).
   *
   * Use the `default` variant in all other cases.
   */
  variant?: 'default' | 'focus-background'
  /** Function to run on close button click */
  onClose: () => void
  title?: string
  subtitle?: string
  secondaryFlyoutContent?: {
    panelTab: 'new' // expecting other variants in the future
    title?: string
    subtitle?: string
    content: ReactElement
  }
}

function BaseFlyout(
  props: BaseFlyoutProps & { 'aria-label': string },
): ReactElement {
  const {
    className = '',
    isOpen,
    variant = 'default',
    children,
    onClose,
    title,
    subtitle,
    secondaryFlyoutContent,
    'aria-label': ariaLabel,
    // ...rest
  } = props

  const [showSecondaryContent, setShowSecondaryContent] = useState(false)

  return (
    <Modal
      className={cn('BaseFlyout-Container', variant)}
      overlayClassName={{
        base: 'BaseFlyout-Overlay',
        afterOpen: 'BaseFlyout-Overlay-open',
        beforeClose: 'BaseFlyout-Overlay-closed',
      }}
      portalClassName={cn('BaseFlyout', className)}
      isOpen={isOpen}
      onRequestClose={onClose}
      closeTimeoutMS={parseInt(flyout_transitionSpeed)}
      bodyOpenClassName="modal-open"
      testId="flyout"
      contentLabel={ariaLabel}
    >
      {secondaryFlyoutContent && (
        <>
          <button
            className={cn(
              'BaseFlyout-PanelTab',
              secondaryFlyoutContent.panelTab,
            )}
            data-testid={'panelTab'}
            onClick={() => setShowSecondaryContent(true)}
          >
            <MagicalAIIcon />
          </button>
          <div
            data-testid={'secondaryFlyout'}
            className={cn('BaseFlyout-Content secondaryFlyout', {
              showSecondaryContent,
            })}
          >
            <div className="BaseFlyout-CloseBar">
              <CloseButton
                data-testid={'closeSecondaryFlyout'}
                className="BaseFlyout-CloseBar-CloseButton"
                variant="dark-on-light"
                onClick={() => setShowSecondaryContent(false)}
              />
            </div>
            {(secondaryFlyoutContent.title ||
              secondaryFlyoutContent.subtitle) && (
              <header className="BaseFlyout-Header">
                {secondaryFlyoutContent.title && (
                  <h2>{secondaryFlyoutContent.title}</h2>
                )}
                {secondaryFlyoutContent.subtitle && (
                  <p>{secondaryFlyoutContent.subtitle}</p>
                )}
              </header>
            )}
            <div className="BaseFlyout-Body">
              {secondaryFlyoutContent.content}
            </div>
          </div>
        </>
      )}
      <div className={'BaseFlyout-Content'} data-testid={'primaryFlyout'}>
        <div className="BaseFlyout-CloseBar">
          <CloseButton
            data-testid={'closePrimaryFlyout'}
            className="BaseFlyout-CloseBar-CloseButton"
            variant="dark-on-light"
            onClick={onClose}
          />
        </div>
        {(title || subtitle) && (
          <header className="BaseFlyout-Header">
            {title && <h2>{title}</h2>}
            {subtitle && <p>{subtitle}</p>}
          </header>
        )}
        <div className="BaseFlyout-Body">{children}</div>
      </div>
    </Modal>
  )
}

// We have to use `styled` instead of the `css` prop because the `css` prop
// will automatically put the styled-components mangled className in the
// `className` prop on `<Flyout>`. However, that puts the class on the Content,
// preventing us from styling the Overlay. By using `styled`, we are able to
// pass the className to `portalClassName`, which will style the parent of both
// Overlay and Content.
export default styled(BaseFlyout)`
  ${style}
`
